import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Briefcase, Plus, Users as UsersIcon, ChevronRight } from "lucide-react";

const STAGES = ["applied", "screen", "interview", "offer", "hired", "rejected"];
const STAGE_BG = {
  applied: "bg-slate-100 text-slate-700",
  screen: "bg-cyan-100 text-cyan-800",
  interview: "bg-violet-100 text-violet-800",
  offer: "bg-amber-100 text-amber-800",
  hired: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

export default function Recruitment() {
  const [jobs, setJobs] = useState([]);
  const [deps, setDeps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [openJob, setOpenJob] = useState(false);
  const [openCand, setOpenCand] = useState(false);
  const [jobForm, setJobForm] = useState({ title: "", department_id: "", location: "", employment_type: "fulltime", experience_level: "mid", description: "", openings: 1, status: "open" });
  const [candForm, setCandForm] = useState({ full_name: "", email: "", phone: "", stage: "applied", notes: "" });

  const loadJobs = async () => { const [j, d] = await Promise.all([api.get("/jobs"), api.get("/departments")]); setJobs(j.data); setDeps(d.data); };
  const loadCands = async (jid) => { const r = await api.get("/candidates", { params: { job_id: jid } }); setCandidates(r.data); };
  useEffect(() => { loadJobs(); }, []);
  useEffect(() => { if (selected) loadCands(selected.id); }, [selected]);

  const createJob = async () => {
    try { await api.post("/jobs", { ...jobForm, openings: Number(jobForm.openings), department_id: jobForm.department_id || null }); toast.success("Job created"); setOpenJob(false); loadJobs(); }
    catch { toast.error("Failed"); }
  };
  const createCand = async () => {
    try { await api.post("/candidates", { ...candForm, job_id: selected.id }); toast.success("Candidate added"); setOpenCand(false); setCandForm({ full_name: "", email: "", phone: "", stage: "applied", notes: "" }); loadCands(selected.id); loadJobs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const moveStage = async (cid, stage) => {
    try { await api.patch(`/candidates/${cid}/stage`, { stage, notes: "" }); toast.success(`Moved to ${stage}`); loadCands(selected.id); } catch { toast.error("Failed"); }
  };

  const generateOffer = async (cand) => {
    const ctc = prompt(`Annual CTC (₹) for ${cand.full_name}:`, "1200000");
    if (!ctc) return;
    const joining = prompt("Joining date (YYYY-MM-DD):", new Date(Date.now()+14*864e5).toISOString().slice(0,10));
    if (!joining) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hreye_token");
      const resp = await fetch(`${API_URL}/api/recruitment/offer-letter`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidate_id: cand.id, salary_ctc: Number(ctc), joining_date: joining }),
      });
      if (!resp.ok) { toast.error("Failed"); return; }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `offer_${cand.full_name.replace(/ /g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Offer letter generated");
      loadCands(selected.id);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="recruitment-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Lifecycle</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Recruitment</h1>
          <p className="text-sm text-slate-600 mt-1">Open positions and candidate pipeline</p>
        </div>
        <Dialog open={openJob} onOpenChange={setOpenJob}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-job-btn"><Plus className="w-4 h-4 mr-1"/>Post job</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Post a job</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Title</Label><Input value={jobForm.title} onChange={(e) => setJobForm({...jobForm, title: e.target.value})} className="mt-1" data-testid="job-title"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label>
                  <Select value={jobForm.department_id} onValueChange={(v) => setJobForm({...jobForm, department_id: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{deps.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Location</Label><Input value={jobForm.location} onChange={(e) => setJobForm({...jobForm, location: e.target.value})} className="mt-1"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={jobForm.employment_type} onValueChange={(v) => setJobForm({...jobForm, employment_type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="fulltime">Full-time</SelectItem><SelectItem value="parttime">Part-time</SelectItem><SelectItem value="contract">Contract</SelectItem><SelectItem value="intern">Intern</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Openings</Label><Input type="number" value={jobForm.openings} onChange={(e) => setJobForm({...jobForm, openings: e.target.value})} className="mt-1"/></div>
              </div>
              <div><Label>Description</Label><Textarea value={jobForm.description} onChange={(e) => setJobForm({...jobForm, description: e.target.value})} rows={3} className="mt-1"/></div>
            </div>
            <DialogFooter><Button onClick={createJob} disabled={!jobForm.title} className="bg-slate-900" data-testid="job-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-[340px_1fr] gap-4">
        <div className="space-y-2">
          {jobs.length === 0 && <Card className="p-8 text-center border-slate-200 shadow-none"><Briefcase className="w-8 h-8 mx-auto text-slate-300 mb-2"/><div className="text-sm text-slate-500">No open jobs.</div></Card>}
          {jobs.map(j => (
            <Card key={j.id} onClick={() => setSelected(j)} className={`p-3 cursor-pointer border shadow-none transition ${selected?.id === j.id ? "border-cyan-500 bg-cyan-50/40" : "border-slate-200 hover:border-cyan-400"}`} data-testid={`job-${j.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-slate-900 text-sm">{j.title}</div>
                <Badge className={`${j.status === "open" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"} border-0 text-[10px]`}>{j.status}</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-2"><span>{j.location}</span>·<span className="capitalize">{j.employment_type}</span>·<span>{j.openings} opening(s)</span></div>
              <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><UsersIcon className="w-3 h-3"/>{j.candidate_count || 0} candidates <ChevronRight className="w-3 h-3 ml-auto"/></div>
            </Card>
          ))}
        </div>

        {selected ? (
          <Card className="border-slate-200 shadow-none">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-[Outfit] font-semibold text-lg text-slate-900">{selected.title}</div>
                <div className="text-xs text-slate-500">{candidates.length} candidates in pipeline</div>
              </div>
              <Dialog open={openCand} onOpenChange={setOpenCand}>
                <DialogTrigger asChild><Button size="sm" className="bg-slate-900" data-testid="new-cand-btn"><Plus className="w-4 h-4 mr-1"/>Add candidate</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add candidate</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div><Label>Full name</Label><Input value={candForm.full_name} onChange={(e) => setCandForm({...candForm, full_name: e.target.value})} className="mt-1" data-testid="cand-name"/></div>
                    <div><Label>Email</Label><Input type="email" value={candForm.email} onChange={(e) => setCandForm({...candForm, email: e.target.value})} className="mt-1"/></div>
                    <div><Label>Phone</Label><Input value={candForm.phone} onChange={(e) => setCandForm({...candForm, phone: e.target.value})} className="mt-1"/></div>
                  </div>
                  <DialogFooter><Button onClick={createCand} disabled={!candForm.full_name || !candForm.email} className="bg-slate-900" data-testid="cand-submit">Add</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 p-3">
              {STAGES.map(st => {
                const col = candidates.filter(c => c.stage === st);
                return (
                  <div key={st} className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 text-center">{st} ({col.length})</div>
                    <div className="space-y-2">
                      {col.map(c => (
                        <Card key={c.id} className={`p-2 shadow-none border ${STAGE_BG[st]} border-transparent`} data-testid={`cand-${c.id}`}>
                          <div className="text-xs font-medium text-slate-900 truncate">{c.full_name}</div>
                          <div className="text-[10px] text-slate-600 truncate">{c.email}</div>
                          <Select value={c.stage} onValueChange={(v) => moveStage(c.id, v)}>
                            <SelectTrigger className="mt-1 h-6 text-[10px] border-0 bg-white/50"><SelectValue/></SelectTrigger>
                            <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s} className="capitalize text-xs">{s}</SelectItem>)}</SelectContent>
                          </Select>
                          {(st === "interview" || st === "offer") && (
                            <button onClick={() => generateOffer(c)} className="mt-1 w-full text-[10px] bg-slate-900 text-white rounded py-0.5 hover:bg-slate-800" data-testid={`offer-${c.id}`}>
                              Generate offer PDF
                            </button>
                          )}
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ) : (
          <Card className="p-10 text-center border-slate-200 shadow-none">
            <Briefcase className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
            <div className="text-sm text-slate-500">Select a job to see candidates</div>
          </Card>
        )}
      </div>
    </div>
  );
}
